import React from 'react';
import { Link } from 'react-router-dom';

import Layout from '../components/layout/Layout';
import paypigImage from '../assets/paypig.png';

const Home: React.FC = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-dark to-dark-dark text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <img
                src={paypigImage}
                alt="PayPig Mascot"
                className="w-32 h-32 object-contain"
              />
            </div>
            <h1 className="text-5xl font-bold mb-6 text-primary glow-text">SpinDom</h1>
            <p className="text-xl mb-8">
              Spin the wheel, drain the ATM, or make them guess. Either way, someone is paying up.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/create-wheel" className="btn-primary">
                🎡 Create Wheel
              </Link>
              <Link
                to="/create-atm"
                className="btn border-2 border-blue-500 bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500"
              >
                🏧 Create ATM
              </Link>
              <Link
                to="/create-paydle"
                className="btn border-2 border-green-500 bg-green-600 text-white hover:bg-green-700 focus:ring-green-500"
              >
                🔤 Create Paydle
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4 text-dark">
            Three Powerful Tools, One Platform
          </h2>
          <p className="text-lg text-center mb-12 text-gray-600 max-w-3xl mx-auto">
            Draining wallets? We've got the tools to make it happen. Free chaos included, premium
            control costs extra (make your sub pay for it).
          </p>

          {/* Wheels Section */}
          <div className="mb-16">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-primary mb-2">🎯 Interactive Wheels</h3>
              <p className="text-gray-600">
                Perfect for decision-making, games, and random selections
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="card p-6 hover:shadow-lg transition-shadow">
                <div className="text-primary text-3xl mb-4">🎡</div>
                <h4 className="text-lg font-semibold mb-2 text-dark">Create Custom Wheels</h4>
                <p className="text-gray-600 mb-4 text-sm">
                  Design interactive wheels with custom entries, themes, and settings - no account
                  required!
                </p>
                <Link
                  to="/create-wheel"
                  className="text-primary hover:text-primary-dark transition-colors text-sm font-medium"
                >
                  Create now →
                </Link>
              </div>

              <div className="card p-6 hover:shadow-lg transition-shadow">
                <div className="text-primary text-3xl mb-4">🔗</div>
                <h4 className="text-lg font-semibold mb-2 text-dark">Share Wheels</h4>
                <p className="text-gray-600 mb-4 text-sm">
                  Generate shareable links for streams, social media, or private use with anyone,
                  anywhere.
                </p>
                <Link
                  to="/wheels"
                  className="text-primary hover:text-primary-dark transition-colors text-sm font-medium"
                >
                  Explore examples →
                </Link>
              </div>

              <div className="card p-6 hover:shadow-lg transition-shadow border-2 border-primary">
                <div className="text-primary text-3xl mb-4">📊</div>
                <h4 className="text-lg font-semibold mb-2 text-dark">
                  Track & Verify{' '}
                  <span className="text-xs bg-primary text-white px-2 py-1 rounded ml-2">
                    PREMIUM
                  </span>
                </h4>
                <p className="text-gray-600 mb-4 text-sm">
                  Enforce spin limits, verify results, and maintain proof of every outcome - no
                  cheating allowed.
                </p>
                <Link
                  to="/sign-up"
                  className="text-primary hover:text-primary-dark transition-colors text-sm font-medium"
                >
                  Sign up free →
                </Link>
              </div>
            </div>
          </div>

          {/* ATMs Section */}
          <div className="mb-16">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-blue-600 mb-2">🏧 Interactive ATMs</h3>
              <p className="text-gray-600">
                Realistic ATM simulations with transaction history and analytics
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="card p-6 hover:shadow-lg transition-shadow">
                <div className="text-blue-600 text-3xl mb-4">🏧</div>
                <h4 className="text-lg font-semibold mb-2 text-dark">Create ATM Simulations</h4>
                <p className="text-gray-600 mb-4 text-sm">
                  Build realistic ATM interfaces with custom transactions, themes, and PIN
                  protection.
                </p>
                <Link
                  to="/create-atm"
                  className="text-blue-600 hover:text-blue-700 transition-colors text-sm font-medium"
                >
                  Create ATM →
                </Link>
              </div>

              <div className="card p-6 hover:shadow-lg transition-shadow">
                <div className="text-blue-600 text-3xl mb-4">📄</div>
                <h4 className="text-lg font-semibold mb-2 text-dark">Transaction History</h4>
                <p className="text-gray-600 mb-4 text-sm">
                  View detailed transaction logs, receipts, and usage statistics for every ATM
                  interaction.
                </p>
                <Link
                  to="/explore-atms"
                  className="text-blue-600 hover:text-blue-700 transition-colors text-sm font-medium"
                >
                  Explore ATMs →
                </Link>
              </div>

              <div className="card p-6 hover:shadow-lg transition-shadow border-2 border-blue-600">
                <div className="text-blue-600 text-3xl mb-4">☁️</div>
                <h4 className="text-lg font-semibold mb-2 text-dark">
                  Cloud Sync & Sharing{' '}
                  <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded ml-2">
                    PREMIUM
                  </span>
                </h4>
                <p className="text-gray-600 mb-4 text-sm">
                  Sync ATMs across devices, advanced sharing controls, and detailed usage analytics.
                </p>
                <Link
                  to="/sign-up"
                  className="text-blue-600 hover:text-blue-700 transition-colors text-sm font-medium"
                >
                  Upgrade now →
                </Link>
              </div>
            </div>
          </div>

          {/* Paydle Section */}
          <div className="border-2 border-green-600 rounded-lg p-6 bg-green-50">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-3 mb-2">
                <h3 className="text-2xl font-bold text-green-600">🔤 Word Challenges (Paydle)</h3>
                <span className="text-xs bg-green-600 text-white px-3 py-1 rounded-full font-semibold">
                  ACCOUNT REQUIRED
                </span>
              </div>
              <p className="text-gray-600">
                Wordle-style word games with stakes - guess right or pay up
              </p>
              <p className="text-sm text-gray-500 mt-2 italic">
                All Paydle features require a free account to create and manage challenges
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="card p-6 hover:shadow-lg transition-shadow border-2 border-green-600">
                <div className="text-green-600 text-3xl mb-4">🎮</div>
                <h4 className="text-lg font-semibold mb-2 text-dark">
                  Create Word Challenges{' '}
                  <span className="text-xs bg-green-600 text-white px-2 py-1 rounded ml-2">
                    PREMIUM
                  </span>
                </h4>
                <p className="text-gray-600 mb-4 text-sm">
                  Set a 5-letter word and a price. Players get 6 tries to guess - fail and they pay!
                </p>
                <Link
                  to="/create-paydle"
                  className="text-green-600 hover:text-green-700 transition-colors text-sm font-medium"
                >
                  Create Paydle →
                </Link>
              </div>

              <div className="card p-6 hover:shadow-lg transition-shadow border-2 border-green-600">
                <div className="text-green-600 text-3xl mb-4">🎯</div>
                <h4 className="text-lg font-semibold mb-2 text-dark">
                  Track Attempts{' '}
                  <span className="text-xs bg-green-600 text-white px-2 py-1 rounded ml-2">
                    PREMIUM
                  </span>
                </h4>
                <p className="text-gray-600 mb-4 text-sm">
                  View every guess your players make and see who wins or pays. Full game history tracked.
                </p>
                <Link
                  to="/create-paydle"
                  className="text-green-600 hover:text-green-700 transition-colors text-sm font-medium"
                >
                  Get started →
                </Link>
              </div>

              <div className="card p-6 hover:shadow-lg transition-shadow border-2 border-green-600">
                <div className="text-green-600 text-3xl mb-4">💰</div>
                <h4 className="text-lg font-semibold mb-2 text-dark">
                  Easy Sharing{' '}
                  <span className="text-xs bg-green-600 text-white px-2 py-1 rounded ml-2">
                    PREMIUM
                  </span>
                </h4>
                <p className="text-gray-600 mb-4 text-sm">
                  Share your word challenges with a simple link. Players need an account to play!
                </p>
                <Link
                  to="/create-paydle"
                  className="text-green-600 hover:text-green-700 transition-colors text-sm font-medium"
                >
                  Try it now →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Experience Comparison - Only visible on mobile */}
      <section className="py-8 bg-dark text-white md:hidden">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-center mb-6 text-primary">
            Choose Your Experience
          </h2>

          <div className="grid grid-cols-1 gap-6 max-w-md mx-auto">
            {/* Basic Experience */}
            <div className="bg-dark-light rounded-lg overflow-hidden shadow-md">
              <div className="p-4 border-b border-gray-700">
                <h3 className="text-xl font-bold mb-1">Basic Experience</h3>
                <p className="text-gray-300 text-sm">Create wheels, ATMs & Paydles with core features</p>
              </div>
              <div className="p-4">
                <div className="mb-4">
                  <h4 className="text-primary font-semibold mb-2 text-sm">🎯 Wheels</h4>
                  <ul className="space-y-1 text-xs">
                    <li className="flex items-center">
                      <svg
                        className="h-3 w-3 text-primary mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Unlimited wheels with themes
                    </li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="text-blue-400 font-semibold mb-2 text-sm">🏧 ATMs</h4>
                  <ul className="space-y-1 text-xs">
                    <li className="flex items-center">
                      <svg
                        className="h-3 w-3 text-blue-400 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Unlimited ATMs with PIN protection
                    </li>
                  </ul>
                </div>
                <div className="mb-4 opacity-50">
                  <h4 className="text-gray-400 font-semibold mb-2 text-sm">
                    🔤 Paydles{' '}
                    <span className="text-xs bg-gray-600 text-white px-2 py-1 rounded ml-1">
                      PREMIUM ONLY
                    </span>
                  </h4>
                  <ul className="space-y-1 text-xs">
                    <li className="flex items-center text-gray-400">
                      <svg
                        className="h-3 w-3 text-gray-400 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Requires account to create & play
                    </li>
                  </ul>
                </div>
                <div className="space-y-2">
                  <Link
                    to="/create-wheel"
                    className="block w-full py-2 px-3 text-center bg-primary hover:bg-primary-dark text-white font-medium rounded transition-colors text-sm"
                  >
                    Create Wheel
                  </Link>
                  <Link
                    to="/create-atm"
                    className="block w-full py-2 px-3 text-center bg-blue-600 hover:bg-blue-700 text-white font-medium rounded transition-colors text-sm"
                  >
                    Create ATM
                  </Link>
                </div>
              </div>
            </div>

            {/* Premium Experience */}
            <div className="bg-dark-light rounded-lg overflow-hidden shadow-md border-2 border-primary transform scale-105">
              <div className="absolute top-0 right-0 bg-primary text-white px-2 py-1 rounded-bl-lg font-bold text-xs">
                RECOMMENDED
              </div>
              <div className="p-4 border-b border-gray-700 bg-primary bg-opacity-20">
                <h3 className="text-xl font-bold mb-1">Power Controller</h3>
                <p className="text-gray-300 text-sm">Full control with cloud storage & analytics</p>
              </div>
              <div className="p-4">
                <div className="mb-4">
                  <h4 className="text-primary font-semibold mb-2 text-sm">🎯 Premium Wheels</h4>
                  <ul className="space-y-1 text-xs">
                    <li className="flex items-center">
                      <svg
                        className="h-3 w-3 text-primary mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Cloud sync & advanced sharing
                    </li>
                    <li className="flex items-center">
                      <svg
                        className="h-3 w-3 text-primary mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Verified results & spin limits
                    </li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="text-blue-400 font-semibold mb-2 text-sm">🏧 Premium ATMs</h4>
                  <ul className="space-y-1 text-xs">
                    <li className="flex items-center">
                      <svg
                        className="h-3 w-3 text-blue-400 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Transaction analytics
                    </li>
                    <li className="flex items-center">
                      <svg
                        className="h-3 w-3 text-blue-400 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Cross-device access
                    </li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="text-green-400 font-semibold mb-2 text-sm">🔤 Word Challenges</h4>
                  <ul className="space-y-1 text-xs">
                    <li className="flex items-center">
                      <svg
                        className="h-3 w-3 text-green-400 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Create & manage Paydles
                    </li>
                    <li className="flex items-center">
                      <svg
                        className="h-3 w-3 text-green-400 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Full game tracking & stats
                    </li>
                  </ul>
                </div>
                <div className="mt-4">
                  <Link
                    to="/sign-up"
                    className="block w-full py-2 px-4 text-center bg-primary hover:bg-primary-dark text-white font-bold rounded transition-colors text-sm"
                  >
                    Sign Up Free
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary-dark to-primary text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to get started?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Create your first interactive experience in minutes - no account required for basic
            features.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-2xl mx-auto">
            <Link
              to="/create-wheel"
              className="btn bg-white text-primary hover:bg-gray-100 transition-colors flex-1"
            >
              🎯 Create Wheel
            </Link>
            <Link
              to="/create-atm"
              className="btn bg-blue-600 text-white hover:bg-blue-700 transition-colors flex-1"
            >
              🏧 Create ATM
            </Link>
            <Link
              to="/create-paydle"
              className="btn bg-green-600 text-white hover:bg-green-700 transition-colors flex-1"
            >
              🔤 Create Paydle
            </Link>
          </div>
          <p className="text-sm mt-4 opacity-75">
            Want advanced features?{' '}
            <Link to="/sign-up" className="underline hover:no-underline">
              Sign up free
            </Link>{' '}
            for cloud storage, analytics, and more.
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
